import { useEffect, useState, type CSSProperties } from 'react';
import { MAX_DETECTION_LOOKBACK_MS, MAX_DETECTION_WINDOW_MS } from '@soc/shared';
import { REPLAY_PRESETS } from './replayPresets';

interface RuleWindowChipsProps {
  /** Currently selected window size in ms. */
  windowMs: number;
  onPick: (windowMs: number) => void;
  /** Custom absolute [from, to] window (ISO 8601), or null when using presets. */
  custom: { from: string; to: string } | null;
  onSetCustom: (from: string, to: string) => void;
  onClearCustom: () => void;
  /** Locked while the rule is running/fetching/frozen — the window is fixed once started. */
  disabled?: boolean;
}

/** Default seed for a fresh custom range: last 24h. */
const DAY_MS = 24 * 3_600 * 1_000;

/** A custom `to` within this slop of now counts as "live" (ends at the present).
 *  2 min covers datetime-local minute precision + brief dwell on the form. */
const ENDS_AT_NOW_SLOP_MS = 2 * 60 * 1_000;

/**
 * True when a custom window's `to` is (near) the present — the only case where a
 * live Start makes sense. A past-ending custom window is forensic-only (Fetch).
 * A null custom ⇒ relative preset ⇒ always ends at now ⇒ true.
 */
export function customWindowEndsNow(custom: { from: string; to: string } | null): boolean {
  if (!custom) return true;
  const to = Date.parse(custom.to);
  if (Number.isNaN(to)) return false;
  return to >= Date.now() - ENDS_AT_NOW_SLOP_MS;
}

/**
 * Why a custom range can't run, or null when it's usable. Shared with RuleCard's
 * Start/Fetch gating so the inline message and the disabled button always agree;
 * mirrors the server-side check in DetectionService (400).
 */
export function customWindowError(custom: { from: string; to: string } | null): string | null {
  if (!custom) return null;
  const from = Date.parse(custom.from);
  const to = Date.parse(custom.to);
  if (!(from < to)) return 'From must be before To';
  if (from < Date.now() - MAX_DETECTION_LOOKBACK_MS) {
    return `From can't be more than ${Math.round(MAX_DETECTION_LOOKBACK_MS / DAY_MS)} days back`;
  }
  if (to - from > MAX_DETECTION_WINDOW_MS) {
    return `Window can't span more than ${Math.round(MAX_DETECTION_WINDOW_MS / DAY_MS)} days`;
  }
  return null;
}

/**
 * Per-rule detection window picker. Relative-only quick-pick chips (1h…7d) or a
 * "Custom" toggle that swaps in two `datetime-local` inputs for an absolute
 * [from, to] window. Start/Fetch backfills from `now - windowMs` (relative) or the
 * custom range; a live rule then ticks forward like any preset.
 */
export function RuleWindowChips({
  windowMs,
  onPick,
  custom,
  onSetCustom,
  onClearCustom,
  disabled = false,
}: RuleWindowChipsProps) {
  const isCustom = custom != null;
  const error = customWindowError(custom);
  const invalid = error != null;

  const enableCustom = () => {
    const now = Date.now();
    onSetCustom(new Date(now - DAY_MS).toISOString(), new Date(now).toISOString());
  };

  return (
    <div style={{ marginTop: 10, opacity: disabled ? 0.5 : 1 }}>
      <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 6 }}>Window</div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
        {REPLAY_PRESETS.map((p) => (
          <Chip
            key={p.label}
            label={p.label}
            active={!isCustom && windowMs === p.ms}
            disabled={disabled}
            onClick={() => {
              // Picking a preset exits custom mode.
              if (isCustom) onClearCustom();
              onPick(p.ms);
            }}
          />
        ))}
        <Chip
          label="Custom"
          active={isCustom}
          disabled={disabled}
          onClick={() => {
            if (!isCustom) enableCustom();
          }}
        />
      </div>

      {isCustom && (
        <div style={{ marginTop: 8, display: 'flex', flexDirection: 'column', gap: 7 }}>
          <DateField
            label="From"
            value={isoToLocalInput(custom.from)}
            invalid={invalid}
            disabled={disabled}
            onChange={(local) => onSetCustom(localInputToIso(local), custom.to)}
          />
          <DateField
            label="To"
            value={isoToLocalInput(custom.to)}
            invalid={invalid}
            disabled={disabled}
            onChange={(local) => onSetCustom(custom.from, localInputToIso(local))}
          />
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <button
              className="chip-link"
              onClick={onClearCustom}
              disabled={disabled}
              style={{
                background: 'none',
                border: 'none',
                padding: 0,
                color: 'var(--text-muted)',
                fontSize: 11,
                cursor: disabled ? 'not-allowed' : 'pointer',
              }}
            >
              ← presets
            </button>
            {error && <span style={{ fontSize: 11, color: '#ff4d4d' }}>{error}</span>}
          </div>
        </div>
      )}
    </div>
  );
}

function DateField({
  label,
  value,
  invalid,
  disabled,
  onChange,
}: {
  label: string;
  value: string;
  invalid: boolean;
  disabled: boolean;
  onChange: (local: string) => void;
}) {
  // While focused, the browser owns the string: echoing the round-tripped
  // (local→ISO→local) value into a controlled datetime-local rewrites the DOM
  // value mid-typing, which resets the segment being edited — e.g. a year typed
  // digit-by-digit ("2" ⇒ 0002) can never be finished. `draft` always mirrors
  // exactly what the browser last reported; the canonical prop is re-adopted on
  // blur. Redux still updates per keystroke, so validation stays live.
  const [draft, setDraft] = useState(value);
  const [focused, setFocused] = useState(false);
  useEffect(() => {
    if (!focused) setDraft(value);
  }, [focused, value]);
  return (
    <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
      <span
        className="eyebrow"
        style={{ width: 32, flex: 'none', fontSize: 10, letterSpacing: 1, color: 'var(--text-muted)' }}
      >
        {label}
      </span>
      <input
        type="datetime-local"
        className="mono dt-field"
        value={draft}
        disabled={disabled}
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
        onChange={(e) => {
          setDraft(e.target.value);
          onChange(e.target.value);
        }}
        style={{ ...dateInputStyle, borderColor: invalid ? '#ff4d4d' : 'var(--border)' }}
      />
    </label>
  );
}

/** Mirrors the RuleParamInput inputs in RuleCard.tsx; grows to fill the card width. */
const dateInputStyle: CSSProperties = {
  flex: 1,
  minWidth: 0,
  background: 'var(--surface-app)',
  border: '1px solid var(--border)',
  borderRadius: 6,
  color: 'var(--text-primary)',
  font: 'inherit',
  fontSize: 12,
  padding: '3px 7px',
};

/**
 * ISO 8601 (UTC) → a "YYYY-MM-DDTHH:mm" local wall-clock string for a
 * `datetime-local` input. Shift by the local timezone offset so the input shows
 * the correct local time.
 */
function isoToLocalInput(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const local = new Date(d.getTime() - d.getTimezoneOffset() * 60_000);
  return local.toISOString().slice(0, 16);
}

/**
 * A "YYYY-MM-DDTHH:mm" local string from the input → ISO 8601 (UTC). The browser
 * parses the naked local string in the local zone, which is exactly what we want.
 * An empty/unparseable value (the field was cleared) returns '' — the caller stores
 * it verbatim; `isoToLocalInput` echoes '' back and the from<to check flags it
 * invalid, so Start/Fetch stays disabled without throwing.
 */
function localInputToIso(local: string): string {
  const d = new Date(local);
  return Number.isNaN(d.getTime()) ? '' : d.toISOString();
}

function Chip({
  label,
  active,
  disabled,
  onClick,
}: {
  label: string;
  active: boolean;
  disabled: boolean;
  onClick: () => void;
}) {
  return (
    <button
      className={active ? 'chip chip--active' : 'chip'}
      onClick={onClick}
      disabled={disabled}
      style={{
        fontSize: 11,
        fontWeight: 600,
        color: active ? 'var(--accent-on)' : 'var(--text-secondary)',
        background: active ? 'var(--accent)' : 'var(--surface-card)',
        border: active ? 'none' : '1px solid var(--border)',
        borderRadius: 7,
        padding: '4px 9px',
        cursor: disabled ? 'not-allowed' : 'pointer',
      }}
    >
      {label}
    </button>
  );
}
